import { TransactionWithInstrumentAndAccount } from "@/types/Transaction";

export interface TransactionCategory {
  name: string;
  transactions: TransactionWithInstrumentAndAccount[];
  total: number;
}

export interface TransactionCategories {
  expenses: TransactionCategory;
  income: TransactionCategory;
  transactions: TransactionCategory;
  cash: TransactionCategory;
}

export function categorizeTransaction(transaction: TransactionWithInstrumentAndAccount): string {
  const accountName = transaction.account.name?.toLowerCase() || '';
  const transactionType = transaction.transactionType?.toLowerCase().trim() || '';
  
  // First priority: Movements due to transactions - Transaction P&L, transactions bought, transactions sold
  // This must come first to catch disposals, acquisitions, etc. before they fall through to amount-based logic
  if (transactionType === 'acquisition' || transactionType === 'disposal' || 
      transactionType === 'deal' || transactionType === 'corporate action' ||
      accountName.includes('acquisition') || accountName.includes('disposal') ||
      accountName.includes('purchase') || accountName.includes('sale') ||
      accountName.includes('p&l') || accountName.includes('pnl') ||
      accountName.includes('trading') || accountName.includes('investment') ||
      accountName.includes('realized') || accountName.includes('unrealized')) {
    return 'transactions';
  }
  
  // Second priority: Income (increases in value) - Dividend income, FIA accounts, other income accruals
  if (transactionType === 'dividend' || transactionType === 'interest' || 
      transactionType === 'return of capital' ||
      accountName.includes('dividend') || accountName.includes('income') ||
      accountName.includes('fia') || accountName.includes('interest') ||
      accountName.includes('yield') || accountName.includes('coupon') ||
      transaction.baseAmount > 0 && (accountName.includes('revenue') || accountName.includes('earning'))) {
    return 'income';
  }
  
  // Third priority: Expenses (decreases in value) - Management fees, tax accrued to date, other expenses
  if (transactionType === 'fee' || transactionType === 'expense' || transactionType === 'accural' ||
      accountName.includes('management') || accountName.includes('fee') || 
      accountName.includes('tax') || accountName.includes('expense') ||
      accountName.includes('admin') || accountName.includes('audit') ||
      transaction.baseAmount < 0 && (accountName.includes('cost') || accountName.includes('charge'))) {
    return 'expenses';
  }
  
  // Fourth priority: Cash movements (capital flows) - Cash receivables, cash payable, cash at bank
  if (transactionType === 'capital call' || transactionType === 'cashflow' || 
      transactionType === 'adjustment' ||
      accountName.includes('cash') || accountName.includes('bank') ||
      accountName.includes('receivable') || accountName.includes('payable') ||
      accountName.includes('capital') || accountName.includes('contribution') ||
      accountName.includes('distribution') || accountName.includes('subscription') ||
      accountName.includes('redemption') || accountName.includes('transfer')) {
    return 'cash';
  }
  
  // Default categorization based on amount direction and fallback logic
  if (transaction.baseAmount < 0) {
    return 'expenses'; // Negative amounts are typically expenses
  } else {
    return 'income'; // Positive amounts are typically income
  }
}

export function groupTransactionsByCategory(transactions: TransactionWithInstrumentAndAccount[]): TransactionCategories {
  const categories: TransactionCategories = {
    expenses: { name: 'Expenses (Decreases in Value)', transactions: [], total: 0 },
    income: { name: 'Income (Increases in Value)', transactions: [], total: 0 },
    transactions: { name: 'Movements due to Transactions', transactions: [], total: 0 },
    cash: { name: 'Cash Movements (Capital Flows)', transactions: [], total: 0 }
  };

  transactions.forEach(transaction => {
    const category = categorizeTransaction(transaction) as keyof typeof categories;
    categories[category].transactions.push(transaction);
    categories[category].total += transaction.baseAmount;
  });

  return categories;
} 